/* ==========================================================================
   EcoFlow Malaysia — motion.js
   --------------------------------------------------------------------------
   The premium scroll-storytelling layer: scroll-linked hero, sticky stages,
   capped parallax, editorial group reveals, masked titles, safe count-up.
   Sits beside theme.js rather than inside it — theme.js is the original,
   always-on behaviour (cart, mega menu, galleries, forms); this is the part
   a merchant can turn off entirely and get back a polished static site.

   One IntersectionObserver decides which scenes are worth paying for. One
   requestAnimationFrame loop reads scroll position exactly once per frame
   and writes exactly one custom property per active scene — every transform,
   opacity and scale downstream of that is CSS doing arithmetic on --ef-p, not
   JS writing inline styles. That split is what keeps this under budget: the
   loop is O(active scenes), not O(everything with a data attribute), and it
   stops entirely the moment nothing is both on screen and worth animating.

   Declarative API used by sections:
     data-ef-motion="<kind>"        scene root — hero | sticky | parallax |
                                     group | mask | count
     data-ef-motion-layer="<role>"  a child the scene drives via --ef-p;
                                     the CSS in that section's own {% style %}
                                     block decides what the role does
     data-ef-motion-speed="<n>"     parallax layers only — a 0..1 multiplier
                                     on --eft-parallax-max
     data-ef-motion-enter="<style>" a one-shot IntersectionObserver reveal for
                                     a whole editorial group — fade | rise |
                                     mask — distinct from the older .eft-reveal
                                     primitive in base.css, which still exists
                                     and is still merchant-editable on its own

   Turned off entirely by prefers-reduced-motion and by the "Enable premium
   motion" theme setting (the latter flips on the .eft-motion-off class in
   theme.liquid, before this file even runs), and by a missing
   IntersectionObserver/rAF (old browsers get the CSS end-state, not a crash).
   The one exception is the header height below — sticky positioning
   elsewhere in the theme depends on that number regardless of whether
   premium motion is on, so it is not something reduced motion should break.
   ========================================================================== */

(function () {
  'use strict';

  var root = document.documentElement;

  /* ── Header height ─────────────────────────────────────────────────────
     Layout plumbing, not an animation — product-nav's sticky rail and the
     PDP gallery's sticky stage both read --ef-header-h to sit correctly
     below the header. Runs unconditionally, before any of the gates below,
     so it is still correct with premium motion off or reduced motion on. */

  var headerEl = document.querySelector('.eft-header');

  function publishHeaderHeight() {
    root.style.setProperty('--ef-header-h', (headerEl ? headerEl.offsetHeight : 0) + 'px');
  }

  if (headerEl) {
    publishHeaderHeight();
    if ('ResizeObserver' in window) {
      new ResizeObserver(publishHeaderHeight).observe(headerEl);
    } else {
      window.addEventListener('resize', publishHeaderHeight);
    }
  }

  if (root.classList.contains('eft-motion-off')) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window) || !('requestAnimationFrame' in window)) return;

  /* ── Config ────────────────────────────────────────────────────────────
     Read once. Numbers live in base.css so design and engineering share one
     source; this file never hard-codes a magnitude. */

  var cfg = (function () {
    var cs = getComputedStyle(root);
    var num = function (name, fallback) {
      var v = parseFloat(cs.getPropertyValue(name));
      return isNaN(v) ? fallback : v;
    };
    var flag = function (id, fallback) {
      var node = document.getElementById('ef-motion-config');
      if (!node) return fallback;
      try {
        var data = JSON.parse(node.textContent);
        return typeof data[id] === 'boolean' ? data[id] : fallback;
      } catch (e) { return fallback; }
    };
    return {
      parallaxMax:  num('--eft-parallax-max', 28),
      scaleFrom:    num('--eft-hero-scale-from', 0.94),
      scaleTo:      num('--eft-hero-scale-to', 1.02),
      motionScale:  num('--eft-motion-scale', 1),
      hero:         flag('hero', true),
      parallax:     flag('parallax', true)
    };
  })();

  /* ── Shared loop ─────────────────────────────────────────────────────────
     scrollY is read exactly once per frame here and handed to every active
     scene and to the header. Nothing else in this file calls
     window.scrollY — that discipline is the whole performance story. */

  var active = new Set();
  var raf = null;
  var lastY = window.scrollY;
  var direction = 0;

  function measure(scene) {
    var r = scene.el.getBoundingClientRect();
    scene.top = r.top + window.scrollY;
    scene.height = r.height;
    scene.viewport = window.innerHeight;
  }

  function progressOf(scene, y) {
    if (scene.kind === 'hero' || scene.kind === 'sticky') {
      /* A pinned scene's own inner stage is sticky, so it is already fully
         visible the moment it engages — there is no "entering from below"
         phase to measure the way a plain card scrolling up the page has.
         What is left to measure is the pin's own extra runway: the scroll
         distance beyond one viewport that the tall wrapper provides before
         the stage releases. 0 the instant that runway starts (the wrapper's
         own top — which, for a hero sitting directly under the header at
         the top of the page, is at or near scrollY 0, correctly leaving the
         scene unsettled until the visitor actually scrolls), 1 the instant
         it ends and the next section is due to take over.

         Measuring this the other way — as if the scene entered from below —
         is what let a hero positioned at the very top of the page start
         already 40% "scrolled through" at scrollY 0, with no scrolling
         having happened at all: scene.top sits only a header's height into
         the page, so the old formula's own viewport-sized entry allowance
         was almost entirely already behind it before the visitor did
         anything. */
      var runway = scene.height - scene.viewport;
      if (runway <= 0) return 1;
      var p1 = (y - scene.top) / runway;
      return p1 < 0 ? 0 : p1 > 1 ? 1 : p1;
    }
    /* Everything else (parallax cards, and any future non-pinned kind) is
       not sticky, so it really does travel the full entering-to-exiting
       distance: 0 the instant its leading edge reaches the viewport bottom,
       1 the instant its trailing edge clears the viewport top. */
    var span = scene.height + scene.viewport;
    var y0 = scene.top - scene.viewport;
    var p = (y - y0) / span;
    return p < 0 ? 0 : p > 1 ? 1 : p;
  }

  function tick() {
    raf = null;
    var y = window.scrollY;
    direction = y > lastY + 2 ? 1 : y < lastY - 2 ? -1 : direction;
    lastY = y;

    updateHeader(y, direction);

    active.forEach(function (scene) {
      var p = progressOf(scene, y);
      scene.el.style.setProperty('--ef-p', p.toFixed(4));
      /* Parallax layers get one extra derived number: a signed px offset,
         zero when the scene is centred in the viewport, capped either side
         by --eft-parallax-max so "subtle independent depth" cannot become
         "the card drifts off its own image". Everything downstream of this
         is CSS reading a variable, same as --ef-p. */
      if (scene.kind === 'parallax') {
        var offset = (p - 0.5) * 2 * scene.speed * cfg.parallaxMax * cfg.motionScale;
        scene.el.style.setProperty('--ef-parallax', offset.toFixed(2) + 'px');
      }
    });

    /* Re-chain only while a heavy scene needs continuous per-frame updates.
       The header alone does not: it only needs to react once per scroll
       event, and the scroll listener below already wakes a fresh tick for
       that. Re-chaining unconditionally would leave a bare rAF loop running
       forever the moment the page is ever scrolled once — the exact
       "continuous animation when nothing is animating" this budget forbids. */
    if (active.size) raf = requestAnimationFrame(tick);
  }

  function wake() {
    if (!raf && !document.hidden) raf = requestAnimationFrame(tick);
  }

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) wake();
  });

  window.addEventListener('scroll', wake, { passive: true });

  /* ── Scene registry ───────────────────────────────────────────────────── */

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      var scene = entry.target._efScene;
      if (!scene) return;
      if (entry.isIntersecting) {
        measure(scene);
        scene.el.style.willChange = 'transform, opacity';
        active.add(scene);
        wake();
      } else {
        active.delete(scene);
        scene.el.style.willChange = '';
      }
    });
  }, { rootMargin: '35% 0px 35% 0px', threshold: 0 });

  var ro = ('ResizeObserver' in window)
    ? new ResizeObserver(function (entries) {
        entries.forEach(function (entry) {
          var scene = entry.target._efScene;
          if (scene && active.has(scene)) measure(scene);
        });
      })
    : null;

  function registerScene(el) {
    if (el._efScene) return;
    var kind = el.getAttribute('data-ef-motion');
    if (kind === 'hero' && !cfg.hero) return;
    if (kind === 'parallax' && !cfg.parallax) return;
    var speed = parseFloat(el.getAttribute('data-ef-motion-speed'));
    var scene = {
      el: el, kind: kind, top: 0, height: 0, viewport: 0,
      speed: isNaN(speed) ? 0.4 : speed
    };
    el._efScene = scene;
    el.style.setProperty('--ef-p', '0');
    io.observe(el);
    if (ro) ro.observe(el);
  }

  function unregisterScene(el) {
    if (!el._efScene) return;
    active.delete(el._efScene);
    io.unobserve(el);
    if (ro) ro.unobserve(el);
    delete el._efScene;
  }

  function initScenes(scope) {
    (scope || document).querySelectorAll('[data-ef-motion]').forEach(registerScene);
  }

  /* ── Header hide-on-scroll ─────────────────────────────────────────────
     Runs inside the same loop rather than a second listener. Stays visible
     near the top of the page and while a mega panel or drawer is open —
     sliding a header out from under an open menu reads as broken, not tidy.
     headerEl and its height are already published above. */

  function interactionOpen() {
    return !!document.querySelector('[data-mega-trigger][aria-expanded="true"]') ||
           !!document.querySelector('.eft-drawer.is-open');
  }

  function updateHeader(y, dir) {
    if (!headerEl) return;
    if (interactionOpen() || y <= headerEl.offsetHeight) {
      headerEl.classList.remove('is-hidden');
    } else if (dir > 0) {
      headerEl.classList.add('is-hidden');
    } else if (dir < 0) {
      headerEl.classList.remove('is-hidden');
    }
  }

  /* ── One-shot group entrances (data-ef-motion-enter) ────────────────────
     The editorial equivalent of .eft-reveal: a whole heading-plus-media group
     enters together, once, rather than each paragraph animating on its own —
     product-story asks for exactly this after its own cleanup script runs. */

  var enterIo = new IntersectionObserver(function (entries, obs) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-in');
      obs.unobserve(entry.target);
    });
  }, { threshold: 0, rootMargin: '0px 0px -10% 0px' });

  function initEnter(scope) {
    (scope || document).querySelectorAll('[data-ef-motion-enter]:not(.is-in)').forEach(function (el) {
      enterIo.observe(el);
    });
  }

  /* ── Count-up (data-ef-motion-count) ─────────────────────────────────────
     Only ever touches the digits. "Up to 95%", "1,129", "No. 1", "5 years" —
     the prefix, suffix, thousands separator, decimal and any words around the
     number are captured once and reprinted verbatim; only the numeric run is
     interpolated. Anything that does not parse to a single clean number is
     left exactly as authored rather than guessed at. */

  var NUM_RE = /-?\d[\d,]*\.?\d*/;

  function initCount(scope) {
    (scope || document).querySelectorAll('[data-ef-motion-count]:not(.is-in)').forEach(function (el) {
      var text = el.textContent;
      var m = NUM_RE.exec(text);
      if (!m) { el.classList.add('is-in'); return; }

      /* "1 of 5" has two numeric runs; animating only the first would count
         up to a number that is not the one actually printed at rest. Safer
         to show the authored text untouched than guess which run matters. */
      var all = text.match(new RegExp(NUM_RE.source, 'g'));
      if (all && all.length > 1) { el.classList.add('is-in'); return; }

      var raw = m[0];
      var end = parseFloat(raw.replace(/,/g, ''));
      if (isNaN(end)) { el.classList.add('is-in'); return; }

      var decimals = (raw.split('.')[1] || '').length;
      var grouped = raw.indexOf(',') !== -1;
      var before = text.slice(0, m.index);
      var after = text.slice(m.index + raw.length);

      var obs = new IntersectionObserver(function (entries, o) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          o.unobserve(el);
          el.classList.add('is-in');
          run(el, end, decimals, grouped, before, after);
        });
      }, { threshold: 0.4 });
      obs.observe(el);
    });
  }

  function fmt(n, decimals, grouped) {
    var s = n.toFixed(decimals);
    if (!grouped) return s;
    var parts = s.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  }

  function run(el, end, decimals, grouped, before, after) {
    var duration = 900, start = null;
    function step(ts) {
      if (start === null) start = ts;
      var p = Math.min(1, (ts - start) / duration);
      var eased = 1 - Math.pow(1 - p, 3);
      el.textContent = before + fmt(end * eased, decimals, grouped) + after;
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  /* ── Boot ────────────────────────────────────────────────────────────── */

  function init(scope) {
    initScenes(scope);
    initEnter(scope);
    initCount(scope);
  }

  init();

  /* Shopify's theme editor swaps section markup without a page load, so
     anything registered on first paint has to be re-registered here or a
     section arrives inert the moment a merchant edits it. */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('[data-ef-motion]').forEach(unregisterScene);
  });
})();
